import "./style-rtl.scss";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import {
    getProductLink,
    sendOTP,
    verifyOTP,
} from "../../universalData";
import { useNavigate, useParams } from "react-router-dom";
import { getAjax, postAjax } from "../../axiosMethods";
import ButtonPrimary from "../ButtonPrimary";
import { phoneValidator } from "../../helperFunctions";
import { ClipLoader } from "react-spinners";
import ButtonSecondary from "../ButtonSecondary";
import { GlobalStateContext } from "./../../../layouts/GlobalStates";
import { applyTokenResponse } from "../../../helperData/panelRoutes";

const OTP_LENGTH = 5;

const normalizeDigits = (value = "") =>
    value
        .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
        .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));

export default function Modal() {
    const { feedbackAppGlobalState, setAuth } = useContext(GlobalStateContext);
    const [result, setResult] = feedbackAppGlobalState;
    const navigate = useNavigate();

    const { id, isIntro } = useParams();
    const [description, setDescription] = useState();
    const [stage, setStage] = useState(1);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [debugCode, setDebugCode] = useState("");
    const [phoneNumberErr, setPhoneNumberErr] = useState("");
    const [otpErr, setOtpErr] = useState("");
    const [otpTimer, setOtpTimer] = useState(0);
    const [loader, setLoader] = useState(false);
    const [otpLoader, setOtpLoader] = useState(false);

    const formattedPhone = () => "+98" + phoneNumber.substr(1);

    const sendOtpHandle = () => {
        setPhoneNumberErr("");
        if (phoneValidator(phoneNumber) === true) {
            sendPhoneNumber();
        } else {
            setPhoneNumberErr(phoneValidator(phoneNumber));
        }
    };

    const verifyOtpHandle = () => {
        setOtpLoader(true);
        postAjax(verifyOTP.replace(":id", isIntro), {
            phone_number: formattedPhone(),
            otp_code: normalizeDigits(otpCode),
        })
            .then((response) => {
                try {
                    applyTokenResponse(setAuth, response);
                } catch (error) {
                    // The visitor still continues into the product page.
                }
                setResult({
                    ...result,
                    username: response.user?.username || formattedPhone(),
                });
                navigate("/feedback/" + id);
            })
            .catch(() => {
                setOtpErr(t("otp.err"));
            })
            .finally(() => {
                setOtpLoader(false);
            });
    };

    const sendPhoneNumber = () => {
        setLoader(true);
        postAjax(sendOTP.replace(":id", isIntro), {
            phone_number: formattedPhone(),
        })
            .then((response) => {
                setStage(2);
                setOtpErr("");
                setOtpCode("");
                setDebugCode(response?.otp_debug_code || "");
                setOtpTimer(120);
            })
            .catch(() => {
                setPhoneNumberErr(t("share.otp.send.error"));
            })
            .finally(() => {
                setLoader(false);
            });
    };

    useEffect(() => {
        getAjax(getProductLink.replace(":id", isIntro)).then((payload) => {
            setDescription(payload.description);
        });
    }, [isIntro]);

    useEffect(() => {
        if (otpTimer > 0) {
            const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [otpTimer]);

    return (
        <div className="floating-modal">
            <div
                className="content"
                role="dialog"
                aria-modal="true"
                aria-labelledby="share-otp-title"
            >
                {stage === 1 && (
                    <div>
                        <h1 id="share-otp-title">{t("message.for.you")}</h1>
                        <h2>{description}</h2>
                        <p>
                            {t("start.rating").replace(
                                "*",
                                "'" + result.ajax.item.name + "'"
                            )}
                        </p>
                        <p className="margin-0">{t("share.otp.login.hint")}</p>
                        <label htmlFor="share-otp-phone">
                            {t("form.phone.title")}
                        </label>
                        <input
                            id="share-otp-phone"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            placeholder={t("form.phone.title")}
                            value={phoneNumber}
                            onChange={(event) =>
                                setPhoneNumber(event.target.value)
                            }
                            className={phoneNumberErr ? "err" : ""}
                            aria-invalid={Boolean(phoneNumberErr)}
                            aria-describedby={
                                phoneNumberErr ? "share-otp-phone-error" : undefined
                            }
                            onFocus={() => setPhoneNumberErr("")}
                        />
                        {phoneNumberErr && (
                            <span id="share-otp-phone-error" className="phone-err">
                                {phoneNumberErr}
                            </span>
                        )}
                        <ButtonPrimary
                            btnContent={
                                loader ? (
                                    <ClipLoader
                                        color="white"
                                        loading={true}
                                        size={30}
                                    />
                                ) : (
                                    t("next.label")
                                )
                            }
                            onClick={sendOtpHandle}
                            disabled={!phoneNumber || loader}
                        />
                    </div>
                )}
                {stage === 2 && (
                    <div className="stage-2">
                        <p>
                            <b>{t("share.msg.on.otp")}</b>
                        </p>
                        <p>{t("enter.code")}</p>
                        {debugCode && (
                            <div className="share-otp-debug" role="status">
                                <span>{t("share.otp.debug")}</span>
                                <strong dir="ltr">{debugCode}</strong>
                            </div>
                        )}
                        <label htmlFor="share-otp-code" className="sr-only">
                            {t("enter.code")}
                        </label>
                        <div>
                            <input
                                id="share-otp-code"
                                maxLength={OTP_LENGTH}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={otpCode}
                                onChange={(event) =>
                                    setOtpCode(
                                        normalizeDigits(event.target.value).replace(
                                            /\D/g,
                                            ""
                                        )
                                    )
                                }
                                className={otpErr ? "err" : ""}
                                aria-invalid={Boolean(otpErr)}
                                onFocus={() => setOtpErr("")}
                            />
                            <button
                                type="button"
                                disabled={otpTimer > 0 || loader}
                                onClick={sendPhoneNumber}
                            >
                                {loader ? (
                                    <ClipLoader
                                        color="white"
                                        loading={true}
                                        size={30}
                                    />
                                ) : otpTimer ? (
                                    otpTimer
                                ) : (
                                    t("send.again")
                                )}
                            </button>
                        </div>
                        {otpErr && <span className="phone-err">{otpErr}</span>}
                        <ButtonPrimary
                            btnContent={
                                otpLoader ? (
                                    <ClipLoader
                                        color="white"
                                        loading={true}
                                        size={30}
                                    />
                                ) : (
                                    t("enter.label")
                                )
                            }
                            disabled={otpCode.length !== OTP_LENGTH || otpLoader}
                            onClick={verifyOtpHandle}
                        />
                        <ButtonSecondary
                            btnContent={t("goto.back")}
                            onClick={() => setStage(1)}
                        />
                    </div>
                )}
            </div>
            <div className="backdrop"></div>
        </div>
    );
}
